// API client for making requests to the worker

// Production API URL
const API_BASE = import.meta.env.PROD ? 'https://api.watching-whales.com' : '';

class ApiClient {
    async request<T>(method: string, path: string, body?: unknown): Promise<T> {
        const url = `${API_BASE}${path}`;

        const options: RequestInit = {
            method,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        };

        if (body) {
            options.body = JSON.stringify(body);
        }

        const response = await fetch(url, options);

        if (!response.ok && response.status === 401) {
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }

        return response.json() as Promise<T>;
    }

    get<T>(path: string): Promise<T> {
        return this.request<T>('GET', path);
    }

    post<T>(path: string, body: unknown): Promise<T> {
        return this.request<T>('POST', path, body);
    }

    put<T>(path: string, body: unknown): Promise<T> {
        return this.request<T>('PUT', path, body);
    }

    delete<T>(path: string): Promise<T> {
        return this.request<T>('DELETE', path);
    }
}

export const api = new ApiClient();

// Typed API methods
export interface AlertsResponse {
    success: boolean;
    data: {
        alerts: Alert[];
        pagination: Pagination;
    };
}

export interface AlertDetailResponse {
    success: boolean;
    data: {
        alert: Alert;
        relatedTrades: Trade[];
    };
}

export interface WalletProfileResponse {
    success: boolean;
    data: {
        wallet: Wallet;
        stats: WalletStats | null;
        recentTrades: Trade[];
        topMarkets: TopMarket[];
    };
}

export interface Alert {
    id: string;
    tradeId: string;
    walletAddress: string;
    marketId: string;
    score: number;
    scoreWallet: number;
    scoreSize: number;
    scoreTiming: number;
    scorePriceEdge: number;
    severity: 'low' | 'medium' | 'high' | 'critical';
    slackSent: boolean;
    createdAt: number;
    trade: Trade;
    wallet: Wallet;
    walletStats: WalletStats | null;
    market: Market;
}

export interface Trade {
    id: string;
    marketId: string;
    marketSlug: string | null;
    marketQuestion: string | null;
    walletAddress: string;
    side: 'YES' | 'NO';
    price: number;
    size: number;
    timestamp: number;
}

export interface Wallet {
    address: string;
    label: string | null;
    classification: 'whale' | 'sharp' | 'normal' | 'unknown';
    firstSeen: number | null;
    lastSeen: number | null;
}

export interface WalletStats {
    walletAddress: string;
    totalVolume: number;
    realizedPnl: number;
    winCount: number;
    lossCount: number;
    marketsTraded: number;
    avgTradeSize: number;
}

export interface Market {
    id: string;
    slug: string | null;
    question: string | null;
    category: string | null;
    endDate: number | null;
    outcome: string | null;
}

export interface TopMarket {
    market: Market;
    trades: number;
    totalVolume: number;
    pnl: number;
}

export interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface CollectionLog {
    id: string;
    startedAt: number;
    finishedAt: number;
    tradesFetched: number;
    tradesSignificant: number;
    tradesProcessed: number;
    alertsCreated: number;
    errors: number;
    status: 'success' | 'error' | 'running';
    errorMessage?: string;
}
